import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import {
  AppointmentProposalOptionCreate,
  AppointmentProposalOptionUpdate,
  useAppointmentProposalOptions,
} from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import ROUTE_PATHS from '../../../routes/paths';
import { OPTION_TEMPID_PREFIX, OptionFormInputs } from './optionForm.models';

type CreatePayload = Required<
  Pick<AppointmentProposalOptionCreate, 'name' | 'optionType' | 'sortOrder'>
>;

type UpdatePayload = AppointmentProposalOptionUpdate & {
  appointmentProposalOptionId: string;
};

export const useOptionsUpsert = () => {
  const optionForm = useFormContext<OptionFormInputs>();
  const navigate = useNavigate();
  const [isUpserting, setIsUpserting] = useState(false);

  const {
    options,
    currentOption,
    createAppointmentProposalOptions,
    updateAppointmentProposalOption,
  } = useAppointmentProposalOptions();
  const { proposalProducts } = useAppointmentProposalOptionProducts();
  const { proposalModifiers } = useAppointmentProposalOptionModifiers();
  const { currentProposal, appendSelectedAppointmentProposalOptionsIds } =
    useAppointmentProposals();

  const buildUpsertPayload = useCallback(
    (formData: OptionFormInputs) => {
      const updatedOptionsMap = new Map(
        formData.names
          .filter((name) => !name.optionId.startsWith(OPTION_TEMPID_PREFIX))
          .map((o) => [o.optionId, o])
      );

      const toUpdate = options.flatMap((o) => {
        const updated = updatedOptionsMap.get(o.appointmentProposalOptionId);
        return updated
          ? updated.name !== o.name || updated.sortOrder !== o.sortOrder
            ? [
                {
                  appointmentProposalOptionId: o.appointmentProposalOptionId,
                  status: o.status,
                  name: updated.name,
                  sortOrder: updated.sortOrder,
                },
              ]
            : []
          : [
              {
                appointmentProposalOptionId: o.appointmentProposalOptionId,
                name: o.name,
                sortOrder: o.sortOrder,
                status: 'DELETED' as const,
              },
            ];
      });

      const toCreate = formData.names
        .filter((name) => !updatedOptionsMap.has(name.optionId))
        .map((option) => ({
          name: option.name,
          sortOrder: option.sortOrder,
          optionType: formData.optionType,
        }));

      const optionsWWithItems = new Set(
        [...proposalProducts, ...proposalModifiers]
          .filter((d) => d.status !== 'DELETED')
          .map((op) => op.appointmentProposalOptionId)
      );

      const deletedOptionsWithItems = toUpdate.filter(
        (o) =>
          o.status === 'DELETED' &&
          optionsWWithItems.has(o.appointmentProposalOptionId)
      );

      return {
        toUpdate,
        toCreate,
        deletedOptionsWithItems,
      };
    },
    [options, proposalModifiers, proposalProducts]
  );

  const upsertOptions = useCallback(
    async (opts: { toUpdate: UpdatePayload[]; toCreate: CreatePayload[] }) => {
      const appointmentId = currentProposal?.appointmentId;
      const appointmentProposalId = currentProposal?.appointmentProposalId;
      const jobId = currentProposal?.jobId;
      if (!appointmentId || !appointmentProposalId || !jobId) {
        throw new Error(
          'Cannot upsert options: missing appointment or proposal context.'
        );
      }

      setIsUpserting(true);

      const [creates] = await Promise.all([
        createAppointmentProposalOptions(
          appointmentId,
          appointmentProposalId,
          opts.toCreate.map((option) => ({ jobId, ...option }))
        ),
        Promise.all(
          opts.toUpdate.map(({ appointmentProposalOptionId, ...payload }) =>
            updateAppointmentProposalOption(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              payload
            )
          )
        ),
      ]);

      if (creates.length > 0) {
        await appendSelectedAppointmentProposalOptionsIds(
          creates.map((o) => o.appointmentProposalOptionId)
        );
      }

      const currentOptionId =
        currentOption?.appointmentProposalId === appointmentProposalId
          ? currentOption.appointmentProposalOptionId
          : undefined;

      const extraPath = opts.toUpdate.some(
        (d) =>
          d.appointmentProposalOptionId === currentOptionId &&
          'status' in d &&
          d.status === 'DELETED'
      )
        ? undefined
        : currentOptionId || creates.at(0)?.appointmentProposalOptionId;

      navigate(
        ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId,
          appointmentProposalId,
          extraPath: extraPath || '',
        })
      );

      setIsUpserting(false);
    },
    [
      createAppointmentProposalOptions,
      currentOption?.appointmentProposalId,
      currentOption?.appointmentProposalOptionId,
      currentProposal?.appointmentId,
      currentProposal?.appointmentProposalId,
      currentProposal?.jobId,
      navigate,
      updateAppointmentProposalOption,
      appendSelectedAppointmentProposalOptionsIds,
    ]
  );

  return {
    isUpserting,
    currentProposal,
    options,
    currentOption,
    optionForm,
    buildUpsertPayload,
    upsertOptions,
  };
};
